<script setup>
import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import contactCrmAPI from 'dashboard/api/contactCrm';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
  timeline: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['refresh']);

const isLoading = ref(false);
const showForm = ref(false);
const formData = ref({
  event_type: 'note',
  title: '',
  description: '',
});

const eventTypes = [
  { value: 'note', label: 'Nota', icon: 'i-lucide-sticky-note', color: 'blue' },
  { value: 'call', label: 'Ligação', icon: 'i-lucide-phone', color: 'green' },
  { value: 'email', label: 'Email', icon: 'i-lucide-mail', color: 'amber' },
  { value: 'meeting', label: 'Reunião', icon: 'i-lucide-users', color: 'purple' },
  { value: 'custom', label: 'Outro', icon: 'i-lucide-more-horizontal', color: 'slate' },
];

const formatDate = dateString => {
  if (!dateString) return '--';
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getEventType = type => {
  return eventTypes.find(e => e.value === type) || eventTypes[4];
};

const getEventColorClass = color => {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600',
    slate: 'bg-n-alpha-2 text-n-slate-11',
  };
  return colors[color] || colors.slate;
};

const closeForm = () => {
  showForm.value = false;
  formData.value = { event_type: 'note', title: '', description: '' };
};

const handleSave = async () => {
  if (!formData.value.title.trim()) {
    useAlert('Preencha o título do evento');
    return;
  }

  isLoading.value = true;
  try {
    await contactCrmAPI.createTimelineEvent(props.contactId, formData.value);
    useAlert('Evento criado com sucesso!');
    closeForm();
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao criar evento');
  } finally {
    isLoading.value = false;
  }
};

const handleTogglePin = async event => {
  try {
    await contactCrmAPI.togglePinTimelineEvent(props.contactId, event.id);
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao fixar evento');
  }
};

const handleDelete = async event => {
  if (!confirm('Excluir este evento?')) return;
  try {
    await contactCrmAPI.deleteTimelineEvent(props.contactId, event.id);
    useAlert('Evento excluído!');
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao excluir evento');
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium text-n-slate-12">
        Timeline de Atividades
      </h3>
      <Button
        label="Nova Atividade"
        icon="i-lucide-plus"
        size="sm"
        @click="showForm = true"
      />
    </div>

    <!-- Empty State -->
    <div
      v-if="timeline.length === 0"
      class="flex flex-col items-center justify-center py-12 rounded-xl bg-n-alpha-1"
    >
      <span class="i-lucide-activity size-12 text-n-slate-10 opacity-50" />
      <p class="mt-4 text-sm text-n-slate-11">Nenhuma atividade registrada</p>
    </div>

    <!-- Timeline -->
    <div v-else class="flex flex-col gap-3">
      <div
        v-for="event in timeline"
        :key="event.id"
        class="flex gap-4 p-4 rounded-xl bg-n-solid-2 outline outline-1 transition-all"
        :class="
          event.is_pinned
            ? 'outline-amber-400 ring-1 ring-amber-400/20'
            : 'outline-n-container'
        "
      >
        <!-- Icon -->
        <div
          class="flex-shrink-0 size-10 rounded-full flex items-center justify-center"
          :class="getEventColorClass(getEventType(event.event_type).color)"
        >
          <span
            :class="getEventType(event.event_type).icon"
            class="size-5"
          />
        </div>

        <!-- Content -->
        <div class="flex-1 min-w-0">
          <div class="flex items-start justify-between gap-2">
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ event.title }}
              </p>
              <p v-if="event.description" class="text-xs text-n-slate-11 mt-1">
                {{ event.description }}
              </p>
            </div>
            <div class="flex items-center gap-1 flex-shrink-0">
              <button
                class="size-7 rounded-lg flex items-center justify-center transition-colors"
                :class="
                  event.is_pinned
                    ? 'text-amber-500 bg-amber-500/10'
                    : 'text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="handleTogglePin(event)"
              >
                <span class="i-lucide-pin size-3.5" />
              </button>
              <button
                class="size-7 rounded-lg flex items-center justify-center text-ruby-600 hover:bg-ruby-500/10 transition-colors"
                @click="handleDelete(event)"
              >
                <span class="i-lucide-trash-2 size-3.5" />
              </button>
            </div>
          </div>
          <div class="flex items-center gap-4 mt-2 text-xs text-n-slate-10">
            <span>{{ formatDate(event.occurred_at) }}</span>
            <span v-if="event.user">por {{ event.user.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Modal -->
    <Teleport to="body">
      <div
        v-if="showForm"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-n-alpha-black5 backdrop-blur-sm"
          @click="closeForm"
        />
        <div
          class="relative w-full max-w-md overflow-hidden rounded-2xl bg-n-solid-2 shadow-xl outline outline-1 outline-n-container flex flex-col"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
          >
            <h3 class="text-base font-medium text-n-slate-12">Nova Atividade</h3>
            <button
              class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="closeForm"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <!-- Content -->
          <div class="p-6 flex flex-col gap-4">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-11">Tipo</label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="type in eventTypes"
                  :key="type.value"
                  class="px-3 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center gap-2"
                  :class="
                    formData.event_type === type.value
                      ? 'border-woot-500 bg-woot-500/10 text-woot-600'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                  "
                  @click="formData.event_type = type.value"
                >
                  <span :class="type.icon" class="size-4" />
                  {{ type.label }}
                </button>
              </div>
            </div>

            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">Título</label>
              <input
                v-model="formData.title"
                type="text"
                placeholder="Ex: Ligação para follow-up"
                class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
              />
            </div>

            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">Descrição</label>
              <textarea
                v-model="formData.description"
                rows="3"
                placeholder="Detalhes da atividade..."
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors resize-none"
              />
            </div>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
          >
            <Button
              label="Cancelar"
              variant="ghost"
              color="slate"
              @click="closeForm"
            />
            <Button
              :label="isLoading ? 'Salvando...' : 'Salvar'"
              :is-loading="isLoading"
              :disabled="isLoading"
              @click="handleSave"
            />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
